<template>
    <div
        :class="{'virtual-keyboard-marginer': keyboardOn}"
        class="translit-wrapper"
    >
        <!-- Select languages -->
        <div class="select-lang-wrapper">
            <!-- Input language -->
            <div class="col">
                <select
                    :value="params.inputLang"
                    class="form-select"
                    @change="selectInputLang(params.inputLang, $event.target.value)"
                >
                    <option
                        v-for="(lang, langCode) in languages"
                        :key="langCode"
                        :value="langCode"
                    >
                        {{ lang.flag }}&nbsp;&nbsp;{{ lang.label }}
                    </option>
                </select>
            </div>
            <!-- Swap languages -->
            <button
                class="swap-langs"
                href=""
                @click.prevent="swapLangs($event)"
            >
                <i class="fa-solid fa-right-left"/>
            </button>
            <!-- Output language -->
            <div class="col">
                <select
                    class="form-select"
                    :value="params.outputLang"
                    @change="selectOutputLang(params.outputLang, $event.target.value)"
                >
                    <option
                        v-for="(lang, langCode) in languages"
                        :key="langCode"
                        :value="langCode"
                    >
                        {{ lang.flag }}&nbsp;&nbsp;{{ lang.label }}
                    </option>
                </select>
            </div>
        </div>
        <div class="d-flex flex-column flex-lg-row flex-wrap">
            <div class="col order-0">
                <div class="input-wrapper">
                    <!-- Translit menu -->
                    <div class="translit-menu-wrapper">
                        <div class="translit-menu">
                            <!-- Clear input data -->
                            <button
                                type="button"
                                class="btn"
                                aria-label="Очистить"
                                data-bs-toggle="tooltip"
                                title="Очистить"
                                @click="clearInput($event)"
                            >
                                <i class="fa-solid fa-delete-left"/>
                            </button>
                            <!-- Clear input data -->
                            <button
                                class="btn btn-toggle-keyboard"
                                :class="{'d-none': params.inputLang !== 'geo'}"
                                :title="keyboardOn ? keyboardCloseLabel : keyboardOpenLabel"
                                :aria-label="keyboardOn ? keyboardCloseLabel : keyboardOpenLabel"
                                type="button"
                                data-bs-toggle="tooltip"
                                @click="toggleKeyboard($event)"
                            >
                                <i class="fa fa-keyboard"/>
                            </button>
                        </div>
                    </div>
                    <!-- Translit textarea -->
                    <div
                        class="textarea-wrapper"
                        @input="handleInput"
                        @keypress="handleInput"
                    >
                        <textarea
                            v-model="params.input"
                            class="translit-textarea form-control w-100"
                            :class="{'virtual-keyboard': keyboardOn}"
                            :maxlength="inputMaxLength"
                            placeholder="Вставьте / введите текст..."
                            @input="resizeTextarea"
                            @keypress="resizeTextarea"
                        />
                        <div
                            :class="{'color-e13a3a': params.input.length === inputMaxLength}"
                            class="textarea-info"
                        >
                            {{ params.input.length }}/{{ inputMaxLength }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="col mt-3 mt-lg-0 order-2 order-lg-1">
                <!-- Result wrapper -->
                <div class="result-outer-wrapper">
                    <div
                        :class="{'done': result.text}"
                        class="result-wrapper"
                    >
                        <!-- Error -->
                        <div
                            v-if="errorMessage"
                            class="mb-2 pb-1 flex-grow-1 text-secondary d-flex justify-content-center"
                        >
                            <p
                                class="mb-0"
                                v-html="errorMessage"
                            />
                        </div>
                        <template v-else>
                            <!-- Result itself -->
                            <p
                                id="result-text"
                                class="m-0 flex-grow-1"
                            >
                                {{ result.text }}
                            </p>
                            <!-- Result menu -->
                            <div class="result-menu-wrapper">
                                <div class="result-menu">
                                    <!-- Copy -->
                                    <a
                                        class="copy-to-clipboard"
                                        href=""
                                        data-clipboard-target="#result-text"
                                        data-bs-toggle="tooltip"
                                        title="Копировать"
                                        @click.prevent="copyToClipboard"
                                    >
                                        <i class="fa-regular fa-copy"/>
                                    </a>
                                    <!-- Translate in Yandex -->
                                    <a
                                        class="yandex-translate"
                                        :class="{'d-none': params.outputLang !== 'geo'}"
                                        :href="`https://translate.yandex.ru?text=${result.text}&source_lang=ka&target_lang=ru`"
                                        target="_blank"
                                        data-bs-toggle="tooltip"
                                        title="Перевести в Яндекс Переводчике"
                                    >
                                        <i class="fa-brands fa-yandex"/>
                                    </a>
                                    <!-- Translate in Google -->
                                    <a
                                        class="google-translate"
                                        :class="{'d-none': params.outputLang !== 'geo'}"
                                        :href="`https://translate.google.ru/?sl=ka&tl=ru&text=${result.text}&op=translate`"
                                        target="_blank"
                                        data-bs-toggle="tooltip"
                                        title="Перевести в Google Переводчике"
                                    >
                                        <i class="fa-brands fa-google"/>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </div>
                    <!-- Loader -->
                    <div
                        v-if="loading"
                        class="loader-wrapper"
                    >
                        <span class="spinner spinner-bigger-2 position-absolute pointer-events-none"/>
                    </div>
                </div>
            </div>
            <!-- Proposed input language, if it's assumed that it's incorrect at the moment -->
            <div
                v-if="proposedOriginal"
                class="col-12 order-1 order-lg-2"
            >
                <p class="mt-1 mb-0 language-proposal">
                    Возможно, язык оригинала:
                    <a
                        class="link"
                        href=""
                        @click.prevent="selectInputLang(params.inputLang, proposedOriginal)"
                    >
                        {{ languages[proposedOriginal].label }}
                    </a>
                </p>
            </div>
        </div>

        <!-- Virtual keyboard with an additional Close button -->
        <div class="virtual-keyboard-wrapper">
            <button
                aria-label="Закрыть клавиатуру"
                title="Закрыть клавиатуру"
                type="button"
                class="btn-close"
                data-bs-toggle="tooltip"
                @click="closeKeyboard"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'Translit',
    data() {
        return {
            inputMaxLength: 5000,
            languages: {
                eng: {
                    label: 'Английский',
                    flag: '🇬🇧',
                },
                geo: {
                    label: 'Грузинский',
                    flag: '🇬🇪',
                },
                rus: {
                    label: 'Русский',
                    flag: '🇷🇺',
                },
            },
            params: {
                input: '',
                inputLang: 'geo',
                outputLang: 'eng'
            },
            result: {
                text: '',
            },
            loading: false,
            errorMessage: '',
            abortController: null,
            throttleTimeout: null,
            clipboardInstance: null,
            keyboardOn: false,
            keyboardOpenLabel: 'Открыть виртуальную клавиатуру',
            keyboardCloseLabel: 'Закрыть виртуальную клавиатуру',
            capsLockActive: false,
            mobileScreen: window.screen.width <= 992
        }
    },
    computed: {
        // Proposed language slug for the input data
        proposedOriginal() {
            let proposedOriginal = ''
            if (this.params.input) {
                let firstInputLetter = (this.params.input.match(/[a-zа-яё\u10A0-\u10FF]/imu) ?? [])[0]
                if (firstInputLetter) {
                    if (/[\u10A0-\u10FF]/imu.test(firstInputLetter)) {
                        proposedOriginal = 'geo'
                    } else if (/[a-z]/imu.test(firstInputLetter)) {
                        proposedOriginal = 'eng'
                    } else if (/[а-яё]/imu.test(firstInputLetter)) {
                        proposedOriginal = 'rus'
                    }
                    if (proposedOriginal === this.params.inputLang) {
                        return ''
                    }
                }
            }
            return proposedOriginal
        }
    },
    methods: {
        // Keysets for mapping from other languages by key codes
        getKeyset(keyset) {
            let keysets = {
                standard: {
                    'Backquote': '„', 'Digit1': '1', 'Digit2': '2', 'Digit3': '3', 'Digit4': '4', 'Digit5': '5', 'Digit6': '6', 'Digit7': '7', 'Digit8': '8', 'Digit9': '9', 'Digit0': '0', 'Minus': '-', 'Equal': '=',
                    'KeyQ': 'ქ', 'KeyW': 'წ', 'KeyE': 'ე', 'KeyR': 'რ', 'KeyT': 'ტ', 'KeyY': 'ყ', 'KeyU': 'უ', 'KeyI': 'ი', 'KeyO': 'ო', 'KeyP': 'პ', 'BracketLeft': '[', 'BracketRight': ']', 'Backslash': '~',
                    'KeyA': 'ა', 'KeyS': 'ს', 'KeyD': 'დ', 'KeyF': 'ფ', 'KeyG': 'გ', 'KeyH': 'ჰ', 'KeyJ': 'ჯ', 'KeyK': 'კ', 'KeyL': 'ლ', 'Semicolon': ';', 'Quote': '\'',
                    'KeyZ': 'ზ', 'KeyX': 'ხ', 'KeyC': 'ც', 'KeyV': 'ვ', 'KeyB': 'ბ', 'KeyN': 'ნ', 'KeyM': 'მ', 'Comma': ',', 'Period': '.', 'Slash': '/',
                },
                shift: {
                    'Backquote': '“', 'Digit1': '!', 'Digit2': '@', 'Digit3': '#', 'Digit4': '$', 'Digit5': '%', 'Digit6': '^', 'Digit7': '&', 'Digit8': '*', 'Digit9': '(', 'Digit0': ')', 'Minus': '_', 'Equal': '+',
                    'KeyW': 'ჭ', 'KeyR': 'ღ', 'KeyT': 'თ', 'BracketLeft': '{', 'BracketRight': '}', 'Backslash': '|',
                    'KeyS': 'შ', 'KeyJ': 'ჟ', 'KeyL': '₾', 'Semicolon': ':', 'Quote': '"',
                    'KeyZ': 'ძ', 'KeyC': 'ჩ', 'KeyN': 'N', 'Comma': '<', 'Period': '>', 'Slash': '?',
                }
            }

            return keysets[keyset]
        },

        // The textarea input handler
        handleInput(e) {
            let $textarea = $(e.target)

            // If the virtual keyboard is active
            if ($textarea.hasClass('virtual-keyboard')) {
                e.preventDefault()
                // Get current keyset
                let keys = this.getKeyset(e.shiftKey ? 'shift' : 'standard')
                // Type the symbol into keyboard
                $textarea.getkeyboard().typeIn(keys[e.code] ?? String.fromCharCode(e.charCode), 170, () => {
                    // update the params.input property
                    this.params.input = $textarea.val()
                });
            }

            // Send
            this.throttleTranslit(600)
        },

        // Auto-resizing of the main textarea
        resizeTextarea() {
            // TODO When typing from a keyboard the scroll position does not change,
            // TODO although this is good in case the caret is not at the end of the content
            let scrollPos = $(document).scrollTop()
            let $textarea = $('.translit-textarea')
            $textarea.css('height', 'auto').css('height', `${$textarea[0].scrollHeight}px`)
            window.scrollTo({top: scrollPos, behavior: 'instant'})
        },

        // Cancel open requests, wait for a while and perform transliteration
        throttleTranslit(timeout = null) {
            clearTimeout(this.throttleTimeout)
            this.abortController?.abort()
            if (timeout) {
                this.throttleTimeout = setTimeout(() => this.translit(), timeout)
            } else {
                this.translit()
            }
        },

        // Clear the input data
        clearInput(e) {
            this.params.input = ''
            this.throttleTranslit()
            let $textarea = $('.translit-textarea')
            $textarea.css('height', 'auto')
            // Open the system keyboard only if this is a desktop or the virtual keyboard is turned off
            if (!this.mobileScreen || !this.keyboardOn) {
                $textarea.focus()
            }

            // Hide the current tooltip
            let tooltip = bootstrap.Tooltip.getInstance(e.currentTarget)
            tooltip.hide()
        },

        // Select input language
        selectInputLang(oldValue, newValue) {
            this.params.outputLang = (newValue === 'geo') ? oldValue : 'geo'
            this.params.inputLang = newValue

            // Close virtual keyboard
            if (this.keyboardOn && this.params.inputLang !== 'geo') {
                this.closeKeyboard()
            }

            if (this.params.input) {
                this.throttleTranslit()
            }
        },

        // Select output language
        selectOutputLang(oldValue, newValue) {
            this.params.outputLang = newValue
            this.params.inputLang = (event.target.value === 'geo') ? oldValue : 'geo'
            if (this.params.input) {
                this.throttleTranslit()
            }
        },

        // Swap languages
        swapLangs() {
            let inputLang = this.params.inputLang
            this.params.inputLang = this.params.outputLang
            this.params.outputLang = inputLang

            if (this.params.inputLang !== 'geo') {
                this.closeKeyboard()
            }

            // Transliterate the output text again
            // if (!this.loading && this.result.text) {
            //     this.params.input = this.result.text
            //     this.result.text = ''
            // }

            this.throttleTranslit()
        },

        // Copy the result to the clipboard
        copyToClipboard() {
            let tooltip = bootstrap.Tooltip.getInstance($('.tooltip')[0])
            this.clipboardInstance
                .on('success', function(e) {
                    e.clearSelection()
                    $('.tooltip .tooltip-inner').html(
                        '<i class="fa fa-check-circle text-success me-1"></i>Скопировано'
                    )
                    tooltip.update()
                })
                .on('error', function(e) {
                    e.clearSelection()
                    $('.tooltip .tooltip-inner').html(
                        '<i class="fa fa-xmark-circle text-danger me-1"></i>Не удалось скопировать'
                    )
                    tooltip.update()
                })
        },

        // Toggle the virtual keyboard
        toggleKeyboard(e) {
            // One more value synchronization (in case of back and forward browser redirections)
            this.params.input = $('.translit-textarea').val()
            // Detect if CapsLock is active
            this.capsLockActive = e.getModifierState('CapsLock')
            // Toggle keyboard
            this.keyboardOn = !this.keyboardOn

            this.$nextTick(() => {
                if (this.keyboardOn) {
                    this.initKeyboard()
                } else {
                    this.destroyKeyboard()

                    // Update keyboard button tooltip
                    let $btn = $('.btn-toggle-keyboard');
                    $btn.attr('data-bs-original-title', $btn.attr('title'))
                    let tooltip = bootstrap.Tooltip.getInstance($btn[0])
                    tooltip.update()
                    tooltip.hide()

                    // Focus the textarea again (only for desktops)
                    if (!this.mobileScreen) {
                        $('.translit-textarea').focus()
                    }
                }
            })
        },

        // Close the virtual keyboard
        closeKeyboard(e) {
            this.keyboardOn = false

            this.$nextTick(() => {
                // At this point, keyboard destruction is partly possible, because it's hidden already
                this.destroyKeyboard()

                // If the closing is done using the Close button, focus the textarea again (only for desktops)
                if (!this.mobileScreen && $(e?.target).hasClass('btn-close')) {
                    $('.translit-textarea').focus()
                }

                // Update keyboard button tooltip
                let $btn = $('.btn-toggle-keyboard');
                $btn.attr('data-bs-original-title', $btn.attr('title'))
                tooltip = bootstrap.Tooltip.getInstance($btn[0])
                tooltip.update()
                tooltip.hide()
            })

            // Hide the pressed tooltip
            let tooltip = bootstrap.Tooltip.getInstance(e?.target)
            tooltip?.hide()
        },

        // Make a transliteration
        translit() {
            // Show an empty result instantly if the input text is empty too
            if (!this.params.input.trim()) {
                this.result.text = ''
                this.loading = false

                let $textarea = $('.translit-textarea')
                $textarea.css('height', 'auto')
                // Open the system keyboard only if the virtual keyboard is turned off
                if (!this.mobileScreen || !this.keyboardOn) {
                    $textarea.focus()
                }

                return
            }
            // Show the loading spinner
            this.loading = true
            // Hide errors
            this.errorMessage = ''

            this.abortController = new AbortController()

            // If recaptcha has not been initialized, show an error and skip the request
            if (!grecaptcha) {
                // Show the error message
                this.errorMessage = '<b>Произошла ошибка</b>. Пожалуйста, <a class="link" href="" onclick="location.reload()">перезагрузите страницу</a> и попробуйте снова.'
                // Hide the loading spinner
                this.loading = false
                return
            }
            grecaptcha.ready(() => {
                grecaptcha.execute('6Lcbo5MkAAAAAB8UQm-cCOn8oWoLMUoan1b5yN-u', {action: 'translit'})
                    .then((recaptchaToken) => {
                        // Send an audio creation task to the server
                        axios.post(
                            '/api/tools/translit',
                            {
                                ...this.params,
                                recaptchaToken
                            },
                            {
                                signal: this.abortController.signal
                            }
                        )
                            .then(response => {
                                // Show the results
                                this.result.text = response.data.text

                                // Resize the textarea again (additionally)
                                this.$nextTick(() => this.resizeTextarea())

                                // Reinitialize clipboard functionality
                                this.clipboardInstance?.destroy()
                                if ($('.copy-to-clipboard').length) {
                                    this.clipboardInstance = new ClipboardJS($('.copy-to-clipboard')[0])
                                }
                                // Hide the loading spinner
                                this.loading = false
                            })
                            .catch(error => {
                                if (error.code !== 'ERR_CANCELED') {
                                    console.log(error)
                                    // Show the error message
                                    this.errorMessage = error.response?.data.message
                                    // Hide the loading spinner
                                    this.loading = false
                                }
                            })
                    })
                    .catch((error) => {
                        console.log(error)
                        // Hide the loading spinner
                        this.loading = false
                    })
            })
        },

        // Initialize the virtual keyboard
        initKeyboard() {
            // Get parameters from the data object
            let params = this.params, throttleTranslit = this.throttleTranslit
            let shiftKeyset = this.getKeyset('shift')
            let capsLockActive = this.capsLockActive
            let mobileScreen = this.mobileScreen

            $('.virtual-keyboard')
                .keyboard({
                    language: ['ru'],
                    layout: 'ms-Georgian (QWERTY)',
                    usePreview: false,

                    // Also synchronizes the params.input property on text deleting
                    autoAccept: true,

                    // Prevents inserting symbols, that don't exist in the current key set
                    // BUT doesn't accept TAB symbol as well. Technically it prints in
                    // the textarea, but THE COUNTER DOESN'T UPDATE.
                    // restrictInput: true,

                    // Also fixes a caps bug
                    alwaysOpen : true,

                    initialFocus: !mobileScreen,

                    appendTo: '.virtual-keyboard-wrapper',
                    display : {
                        'tab': '<p class="fs-5 d-inline">→</p> Tab',
                        'lock': '<p class="fs-6 d-inline">⇧</p> Caps Lock',
                        'shift': '<p class="fs-6 d-inline">⇧</p> Shift',
                        'enter': '<p class="fs-5 d-inline">↲</p> Enter',
                        'bksp': '<p class="fs-5 d-inline">←</p>',
                    },

                    css: {
                        // input & preview
                        input          : 'ui-widget-content ui-corner-all',
                        // keyboard container
                        container      : 'ui-widget-content ui-widget ui-corner-all ui-helper-clearfix',
                        // keyboard container extra class (same as container, but separate)
                        popup: '',
                        // default state
                        buttonDefault  : 'ui-state-default ui-corner-all',
                        // hovered button
                        buttonHover    : 'ui-state-hover',
                        // Action keys (e.g., Accept, Cancel, Tab, etc.); this replaces the "actionClass" option
                        buttonAction   : '',
                        // Active keys (e.g., shift down, meta keyset active, combo keys active)
                        buttonActive   : 'ui-state-active',
                        // used when disabling the decimal button {dec}
                        buttonDisabled : 'ui-state-disabled',
                        // empty button class name {empty}
                        buttonEmpty    : 'ui-keyboard-empty'
                    },
                    change: function(event, keyboard, el) {
                        // In case of typing on mobile device through the virtual keyboard
                        if (event?.originalEvent?.type === 'touchstart') {
                            // Make the textarea readonly
                            $('.translit-textarea').attr('readonly', true)
                        }

                        // Synchronize the value again (sometimes they were different at this point)
                        params.input = keyboard.$preview.val()

                        // if that wasn't Tab, Shift or CapsLock key pressed
                        if (!['tab', 'shift', 'lock'].includes(event.action)) {
                            // Make a transliteration
                            throttleTranslit(600)
                        }
                    },
                })
                .addTyping()
                .on('keydown', function(e) {
                    // Custom prohibition of entering characters that do not exist in the current keyboard
                    if (e.code.indexOf('Key') === 0 && e.shiftKey) {
                        if (!shiftKeyset[e.code]) {
                            e.preventDefault()
                        }
                    }

                    // Prevent highlighting of incorrect keys on typing
                    if (e.shiftKey) {
                        // When write @ prevent " key hover (actual for russian keyset)
                        if (e.code === 'Digit2') { $('.ui-keyboard-34').addClass('prevent-hover') }
                        // When write ^ prevent : key hover (actual for russian keyset)
                        if (e.code === 'Digit6') { $('.ui-keyboard-58').addClass('prevent-hover') }
                        // When write & prevent ? key hover (actual for russian keyset)
                        if (e.code === 'Digit7') { $('.ui-keyboard-63').addClass('prevent-hover') }
                    }

                    // Caps Lock fix (emit keypress event)
                    if (e.code === 'CapsLock') {
                        // Actualize CapsLock key status
                        $('.ui-keyboard-lock').toggleClass('ui-state-caps-hover', e.originalEvent.getModifierState('CapsLock'))

                        // Initialize additional keypress to prevent switching of keyset
                        if (!e.preventCaps) {
                            let event = jQuery.Event('keypress', {
                                code: 'CapsLock',
                                preventCaps: true
                            })
                            $('.translit-textarea').trigger(event)
                        }
                    }
                    // Zero button fix (some jQuery UI problems, because of Caps Lock fix)
                    $('.ui-keyboard-0').removeClass('ui-state-hover')
                })
                .on('keyup', function(e) {
                    // When @ is keyup, remove hover preventing of " key (actual for russian keyset)
                    if (e.code === 'Digit2') { setTimeout(() => $('.ui-keyboard-34').removeClass('prevent-hover'), 200) }
                    // When ^ is keyup, remove hover preventing of : key (actual for russian keyset)
                    if (e.code === 'Digit6') { setTimeout(() => $('.ui-keyboard-58').removeClass('prevent-hover'), 200) }
                    // When & is keyup, remove hover preventing of ? key (actual for russian keyset)
                    if (e.code === 'Digit7') { setTimeout(() => $('.ui-keyboard-63').removeClass('prevent-hover'), 200) }
                    // TAB key fixes
                    params.input = $('.translit-textarea').val()
                })
                .on('click select', function() {
                    $(this).removeAttr('readonly')
                })

            // Stop CapsLock actions and actualize its status
            $('.ui-keyboard-lock')
                .attr('data-action', null)
                .toggleClass('ui-state-caps-hover', capsLockActive)
                .on('click', function() {
                    $(this).toggleClass('ui-state-caps-hover', !$(this).hasClass('ui-state-caps-hover'))
                })

            // Update keyboard button tooltip
            let $btn = $('.btn-toggle-keyboard');
            $btn.attr('data-bs-original-title', $btn.attr('title'))
            let tooltip = bootstrap.Tooltip.getInstance($btn[0])
            tooltip.update()
            tooltip.hide()

            // Show the virtual keyboard
            $('.virtual-keyboard-wrapper').addClass('opened')
        },

        // Destroy the virtual keyboard
        destroyKeyboard() {
            // Hide the keyboard
            $('.virtual-keyboard-wrapper').removeClass('opened')
            // Destroy the keyboard when it's hidden
            setTimeout(() => $('.translit-textarea').unbind()?.getkeyboard()?.destroy(), 100)
        }
    },
}
</script>

<style scoped lang="scss">
.spinner {
    position: initial !important;
    @media (width < 1200px) {
        top: unset;
        bottom: -36px;
        left: 50%;
        transform: translateX(-50%);
    }
}
.translit-wrapper {
    margin-bottom: 60px;
    div, select, textarea {
        font-size: 1.1rem;
    }
    .select-lang-wrapper {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        border: 1px solid #ced4da;
        border-radius: .4rem .4rem 0 0;
        gap: 2rem;
        select {
            width: 190px;
            border: none;
            cursor: pointer;
            &:focus {
                box-shadow: 0 0 0 0.2rem rgba(13 110 253 / 25%);
            }
        }
        .col:first-of-type select {
            margin-left: auto;
        }
        .swap-langs {
            border: none;
            margin: 0 .25rem;
            background: none;
            color: #6c757d;
            opacity: .75;
        }
    }
    .input-wrapper {
        position: relative;
        z-index: 1;
        height: 100%;
        .translit-menu-wrapper {
            position: absolute;
            top: 0;
            right: 0;
            height: 100%;
            padding-top: 5px;
            padding-right: 12px;
            .translit-menu {
                position: sticky;
                top: 80px;
                display: flex;
                flex-direction: column;
                margin-bottom: 36px;
                a, button {
                    display: inline-block;
                    width: 22px;
                    height: 22px;
                    box-sizing: content-box;
                    padding: 5px;
                    border-radius: 50%;
                    color: #000;
                    font-size: 16px;
                    opacity: .4;
                    transition: opacity .1s ease;
                    &:active, &:focus, &:focus-visible {
                        border-color: transparent;
                        box-shadow: none;
                    }
                    &:hover {
                        opacity: .9;
                    }
                }
            }
        }
        .textarea-wrapper {
            display: flex;
            height: 100%;
            flex-direction: column;
            .translit-textarea {
                overflow: hidden;
                min-height: 350px;
                flex-grow: 1;
                padding-right: 45px;
                padding-bottom: 30px;
                padding-left: 10px;
                border: 1px solid #ced4da;
                border-radius: 0 0 0 .4rem;
                border-top: none;
                border-bottom: none;
                background: transparent;
                overflow-wrap: break-word;
                resize: none;
                white-space: pre-wrap;
                word-break: break-word;
                word-wrap: break-word;
                &:focus {
                    box-shadow: 0 0 0 0.2rem rgba(13  110 253 / 25%);
                }
            }
            .textarea-info {
                position: absolute;
                right: 0;
                bottom: 0;
                width: 100%;
                height: 24px;
                padding-right: 5px;
                border: 1px solid #ced4da;
                border-radius: 0 0 0 .4rem;
                border-top: 1px solid #dee2e7;
                background: #f3f3f3;
                color: #777;
                font-size: 13px;
                line-height: 20px;
                text-align: right;
            }
        }
    }
    .loader-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        width: 100%;
        height: 100%;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: 0 0 .4rem;
        background: #f9f9f9;
    }
    .result-outer-wrapper {
        position: relative;
        height: 100%;
        border: 1px solid #ced4da;
        border-radius: 0 0 .4rem;
        border-top: none;
        border-left: none;
        .result-wrapper {
            position: relative;
            display: flex;
            height: 100%;
            flex-direction: column;
            padding: 5px 45px 7px 10px;
            border: none;
            border-radius: 0 0 .4rem;
            background: #f9f9f9;
            p {
                overflow-wrap: break-word;
                white-space: pre-wrap;
                word-break: break-word;
                word-wrap: break-word
            }
            .result-menu-wrapper {
                position: absolute;
                top: 0;
                right: 0;
                display: none;
                height: 100%;
                padding-top: 2px;
                padding-right: 12px;
                .result-menu {
                    position: sticky;
                    top: 77px;
                    right: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-end;
                    padding: 4px 0;
                    margin: 0 -5px;
                    background: #f9f9f9;
                    a {
                        padding: 3px 7px;
                        i {
                            color: #999;
                            font-size: 17px;
                            transition: .15s ease;
                            &.fa-copy { font-size: 18px; }
                            &.fa-yandex {
                                position: relative;
                                left: -1px;
                                font-size: 18px;
                                font-weight: bold;
                            }
                            &.fa-google { font-size: 16px; }
                        }
                        &:hover {
                            i.fa-copy { color: #222; }
                            i.fa-yandex { color: #f8604a; }
                            i.fa-google { color: #3b75ef; }
                        }
                    }
                }
            }
            &.done .result-menu-wrapper {
                display: block;
            }
        }
    }
    .language-proposal {
        color: #6c757d;
        font-size: 15px;
    }
}

@media (width < 992px) {
    .translit-wrapper {
        max-height: initial;
        .input-wrapper {
            .textarea-wrapper {
                .translit-textarea {
                    min-height: 35vh;
                }
                .translit-textarea, .textarea-info {
                    border-radius: 0 0 .4rem .4rem;
                }
            }
        }
        .result-outer-wrapper {
            border-radius: .4rem;
            border-top: 1px solid #ced4da;
            border-left: 1px solid #ced4da;
            .result-wrapper {
                min-height: 35vh;
                border-radius: .4rem;
            }
            .loader-wrapper {
                border-radius: .4rem;
            }
        }
    }
}

@media (width < 576px) {
    .translit-wrapper {
        .select-lang-wrapper {
            gap: 5px;
            select {
                width: 100%;
            }
            .col:first-of-type select {
                border-radius: .4rem 0;
            }
            .col:last-of-type select {
                border-radius: 0 .4rem 0 0;
            }
        }
    }
}
</style>
